import { useEffect, useRef } from 'react'

import { env } from '@/config/env'
import {
  isHttpStatusInfrastructureFailure,
  notifyMercureHubLikelyDown,
} from '@/services/serviceReachabilityMonitor'

const BACKOFF_BASE_MS = 1000
const BACKOFF_MAX_MS = 30000

interface UseMercureSseOptions {
  topics: string[]
  mercureToken: string | null
  enabled?: boolean
  onData: (data: string) => void
  onConnectionStateChange?: (connected: boolean) => void
}

/**
 * Shared Mercure SSE transport based on fetch + ReadableStream.
 * Allows Authorization header usage (unlike EventSource).
 */
export function useMercureSse({
  topics,
  mercureToken,
  enabled = true,
  onData,
  onConnectionStateChange,
}: UseMercureSseOptions): void {
  const onDataRef = useRef(onData)
  onDataRef.current = onData

  const onConnectionStateChangeRef = useRef(onConnectionStateChange)
  onConnectionStateChangeRef.current = onConnectionStateChange

  /** Primitive: same topics with a new array instance each render must not reconnect SSE. */
  const topicsSignature =
    Array.isArray(topics) && topics.length > 0
      ? JSON.stringify([...topics].sort())
      : ''

  useEffect(() => {
    const hubUrl = env.MERCURE_PUBLIC_URL
    const ok =
      enabled &&
      !!mercureToken &&
      topicsSignature.length > 0 &&
      typeof hubUrl === 'string'
    if (!ok) return

    const topicList = JSON.parse(topicsSignature) as string[]

    let backoff = BACKOFF_BASE_MS
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let aborted = false
    let abort: () => void = () => {}

    const connect = async () => {
      if (aborted) return
      const params = new URLSearchParams()
      for (const topic of topicList) {
        params.append('topic', topic)
      }
      const url = `${hubUrl}?${params.toString()}`

      const ctrl = new AbortController()
      abort = () => ctrl.abort()

      try {
        const res = await fetch(url, {
          signal: ctrl.signal,
          headers: {
            Accept: 'text/event-stream',
            Authorization: `Bearer ${mercureToken}`,
          },
        })

        if (!res.ok) {
          onConnectionStateChangeRef.current?.(false)
          if (isHttpStatusInfrastructureFailure(res.status)) {
            notifyMercureHubLikelyDown()
          }
          if (!aborted) {
            const delay = Math.min(backoff, BACKOFF_MAX_MS)
            backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
            timeoutId = setTimeout(connect, delay)
          }
          return
        }

        onConnectionStateChangeRef.current?.(true)
        backoff = BACKOFF_BASE_MS
        const reader = res.body?.getReader()
        const dec = new TextDecoder()
        if (!reader) {
          onConnectionStateChangeRef.current?.(false)
          timeoutId = setTimeout(connect, backoff)
          return
        }

        let buffer = ''
        while (true) {
          const { done, value } = await reader.read()
          if (done || aborted) break
          buffer += dec.decode(value, { stream: true })
          const lines = buffer.split(/\r?\n/)
          buffer = lines.pop() ?? ''
          let data = ''
          for (const line of lines) {
            if (line.startsWith('data:')) {
              data = line.slice(5).trim()
            } else if (line === '' && data) {
              onDataRef.current(data)
              data = ''
            }
          }
        }

        onConnectionStateChangeRef.current?.(false)
        if (!aborted) {
          timeoutId = setTimeout(connect, backoff)
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return
        }
        onConnectionStateChangeRef.current?.(false)
        notifyMercureHubLikelyDown()
        if (!aborted) {
          const delay = Math.min(backoff, BACKOFF_MAX_MS)
          backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
          timeoutId = setTimeout(connect, delay)
        }
      }
    }

    void connect()

    return () => {
      aborted = true
      abort()
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      onConnectionStateChangeRef.current?.(false)
    }
  }, [enabled, mercureToken, topicsSignature])
}
